import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikValues } from "formik";
import { opsBulkActionSchema } from "formValidations/opsBulkActionSchema";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useGetAllInkleCPATeamsQuery } from "store/apis/internalTeamSetting";
import { useUpdateBulkTaskMutation } from "store/apis/task";
import { BackendError } from "types/utils/error";
import { UNASSIGNED_OPTION } from "./Task1120FilterOptions";

type Task1120BulkServiceTeamUpdateProps = {
  isOpen: boolean;
  close: () => void;
  selectedRows: string[];
  resetSelection: () => void;
};

export const Task1120BulkServiceTeamUpdateModal = ({
  isOpen,
  close,
  selectedRows,
  resetSelection,
}: Task1120BulkServiceTeamUpdateProps) => {
  const { isAdmin } = useRoleBasedView();
  const { promise: promiseToast } = useToast();
  const { data: teamOptions = [], isLoading } = useGetAllInkleCPATeamsQuery(
    { accessibleTeams: "CPA_TEAM" },
    { skip: !isAdmin }
  );
  const [bulkTaskUpdate, { isLoading: isUpdating }] =
    useUpdateBulkTaskMutation();

  const updatedOptions = [
    { uuid: UNASSIGNED_OPTION, name: "Unassigned" },
    ...teamOptions,
  ];

  const handleUpdate = async (values: FormikValues) => {
    promiseToast(
      () =>
        bulkTaskUpdate({
          payload: {
            task_ids: selectedRows,
            service_team_id:
              values.value === UNASSIGNED_OPTION ? null : values.value,
          },
        }).unwrap(),
      {
        loading: "Updating service team...",
        success: () => {
          close();
          resetSelection();
          return `Sales person updated for ${selectedRows.length} tasks`;
        },
        error: (error) => {
          return (error as BackendError)?.data?.error?.message;
        },
      }
    );
  };

  const options = updatedOptions.map((team) => ({
    value: team.uuid,
    label: team.name,
  }));

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content useCustomOverlay>
          <Modal.Header>
            <Modal.Title>Update Service Team</Modal.Title>
          </Modal.Header>

          <Formik
            initialValues={{ value: "" }}
            onSubmit={handleUpdate}
            enableReinitialize
            validationSchema={opsBulkActionSchema}
          >
            {({ handleSubmit }) => {
              return (
                <Form onSubmit={handleSubmit} className="t-m-0">
                  <Modal.Body>
                    {isLoading ? (
                      <Loader size="small" />
                    ) : (
                      <Combobox
                        withForm
                        name="value"
                        label="Select service team"
                        required
                        menuPortalTarget={document.body}
                        options={options}
                        placeholder="Select"
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            width: "300px",
                          }),
                        }}
                      />
                    )}
                  </Modal.Body>

                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button type="button">Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      type="submit"
                      isLoading={isUpdating}
                    >
                      Assign all ({selectedRows.length})
                    </Button>
                  </Modal.FooterButtonGroup>
                </Form>
              );
            }}
          </Formik>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
